import { memo, ReactNode } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  VStack,
  Flex,
  HStack,
  Heading,
  Text,
  Icon,
  IconButton,
  Tooltip,
  Badge,
  Tag,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react'
import {
  LuCalendar,
  LuBookOpen,
  LuUsers,
  LuFolderPlus,
  LuBookmarkPlus,
  LuBookmark,
  LuExternalLink,
  LuSparkles,
  LuChevronUp,
  LuChevronDown,
} from 'react-icons/lu'
import { Article } from '../types'

interface ArticleCardProps {
  article: Article
  onBookmark?: () => void
  onAddToProject?: () => void
  isBookmarked?: boolean
  cardBg: string
  borderColor: string
  hoverBg: string
  highlightText?: (text: string) => ReactNode
}

export const ArticleCard = memo(function ArticleCard({
  article,
  onBookmark,
  onAddToProject,
  isBookmarked,
  cardBg,
  borderColor,
  highlightText,
}: ArticleCardProps) {
  const { isOpen, onToggle } = useDisclosure()
  const rawAuthors = (article as any)?.authors
  const authorsList = Array.isArray(rawAuthors)
    ? rawAuthors
    : typeof rawAuthors === 'string'
      ? rawAuthors
          .split(',')
          .map((author) => author.trim())
          .filter(Boolean)
      : []

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: 'md', borderColor: 'purple.300' }}
    >
      <CardBody>
        <VStack align="stretch" spacing={3}>
          {/* Title and Actions */}
          <Flex justify="space-between" align="flex-start" gap={4}>
            <VStack align="stretch" spacing={2} flex={1}>
              <Heading size="md" color="brand.sideBarBlack" lineHeight="tall">
                {highlightText ? highlightText(article.title) : article.title}
              </Heading>

              <HStack spacing={2} flexWrap="wrap">
                <HStack spacing={1} fontSize="sm" color="gray.600">
                  <Icon as={LuCalendar} boxSize={3} />
                  <Text>{article.publicationDate}</Text>
                </HStack>
                <Text color="gray.400">•</Text>
                <HStack spacing={1} fontSize="sm" color="gray.600">
                  <Icon as={LuBookOpen} boxSize={3} />
                  <Text>{article.journal}</Text>
                </HStack>
                {authorsList.length > 0 && (
                  <>
                    <Text color="gray.400">•</Text>
                    <HStack spacing={1} fontSize="sm" color="gray.600">
                      <Icon as={LuUsers} boxSize={3} />
                      <Text noOfLines={1}>{authorsList.slice(0, 3).join(', ')}</Text>
                      {authorsList.length > 3 && <Text>et al.</Text>}
                    </HStack>
                  </>
                )}
              </HStack>
            </VStack>

            <HStack spacing={2} flexShrink={0}>
              {onAddToProject && (
                <Tooltip label="Add to MedInsight project">
                  <IconButton
                    aria-label="Add to project"
                    icon={<LuFolderPlus />}
                    variant="ghost"
                    colorScheme="green"
                    onClick={onAddToProject}
                  />
                </Tooltip>
              )}

              {!isBookmarked && onBookmark && (
                <Tooltip label="Bookmark this article">
                  <IconButton
                    aria-label="Bookmark"
                    icon={<LuBookmarkPlus />}
                    variant="ghost"
                    colorScheme="purple"
                    onClick={onBookmark}
                  />
                </Tooltip>
              )}

              {isBookmarked && (
                <Badge colorScheme="purple" variant="subtle">
                  <HStack spacing={1}>
                    <Icon as={LuBookmark} boxSize={3} />
                    <Text>Bookmarked</Text>
                  </HStack>
                </Badge>
              )}

              <Tooltip label="View on PubMed">
                <IconButton
                  aria-label="View on PubMed"
                  icon={<LuExternalLink />}
                  variant="ghost"
                  colorScheme="blue"
                  as="a"
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </Tooltip>
            </HStack>
          </Flex>

          {/* Keywords */}
          {article.keywords && article.keywords.length > 0 && (
            <HStack spacing={2} flexWrap="wrap">
              {article.keywords.slice(0, 5).map((keyword, idx) => (
                <Tag key={idx} size="sm" colorScheme="purple" variant="subtle">
                  {keyword}
                </Tag>
              ))}
              {article.keywords.length > 5 && (
                <Tag size="sm" colorScheme="gray" variant="subtle">
                  +{article.keywords.length - 5} more
                </Tag>
              )}
            </HStack>
          )}

          {/* AI Summary */}
          {article.aiSummary && (
            <Box
              bg="purple.50"
              borderRadius="md"
              p={3}
              borderWidth="1px"
              borderColor="purple.200"
            >
              <HStack spacing={2} mb={2}>
                <Icon as={LuSparkles} color="purple.500" />
                <Text fontWeight="semibold" color="purple.700" fontSize="sm">
                  AI Summary
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.700">
                {article.aiSummary}
              </Text>
            </Box>
          )}

          {/* Abstract (Collapsible) */}
          <Box>
            <Button
              size="sm"
              variant="ghost"
              rightIcon={isOpen ? <LuChevronUp /> : <LuChevronDown />}
              onClick={onToggle}
            >
              {isOpen ? 'Hide' : 'Show'} Abstract
            </Button>

            <Collapse in={isOpen} animateOpacity>
              <Box mt={2} pl={4} borderLeftWidth="2px" borderLeftColor="gray.300">
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  {highlightText ? highlightText(article.abstract) : article.abstract}
                </Text>
              </Box>
            </Collapse>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  )
})
